import React, { useState, useRef, useEffect, useMemo } from 'react'
import DashboardHeader from '../../components/DashboardHeader'
import { FiPlusCircle, FiEye, FiShare2, FiCopy } from 'react-icons/fi'
import { RxPencil1 } from 'react-icons/rx'
import { useHistory } from 'react-router-dom'
import ServiceOrderFilter from '../../components/Filters/ServiceOrderFilter'
import Spinner from '../../components/Spinner'
import Gauge from '../../components/Gauge'
import { IoIosArrowRoundBack } from 'react-icons/io'
import DeleteGroupModal from '../../components/modals/DeleteGroupModal'
import { Tooltip } from '../../layout/sidebar'
import { IoBagHandleOutline } from 'react-icons/io5'
import { BsBox } from 'react-icons/bs'
import ProductDetailModal from '../../components/modals/ProductDetailModal'
import ServiceDetailModal from '../../components/modals/ServiceDetailModal'
import useSWR, { mutate } from 'swr'
import api, { fetcher } from '../../utils/api'
import { ErrorNotification, SuccessNotification } from '../../utils'
import { shareMarketplaceItemLink, shareStorefrontLink, copyStorefrontLink, resolveStorefrontIdentifier, canShareStorefront } from '../../utils/marketplace'
import useAuth from '../../context/userContext'


const ServiceOrdersInventory = () => {
  const { user } = useAuth()
  const [states, setStates] = useState({
    search: "",
    page_count: 10,
    page: 1,
    sort: "desc",
    sort_by: "created_at",
    category: "",
    country: "",
    region: "",
    community: "",
    visibility: "",
    productType: "All Types"
  })
  const history = useHistory()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [showProductModal, setShowProductModal] = useState(false)
  const [showServiceModal, setShowServiceModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [filteredSuggestions, setFilteredSuggestions] = useState([])
  const inputRef = useRef(null)

  // Build query string from filter states
  const buildQueryString = () => {
    const params = new URLSearchParams()
    if (states.search) params.append('search', states.search)
    if (states.category) params.append('category', states.category)
    if (states.country) params.append('country', states.country)
    if (states.region) params.append('region', states.region)
    if (states.community) params.append('community', states.community)
    if (states.visibility) params.append('is_active', states.visibility)
    if (states.sort) params.append('ordering', `${states.sort_by}${states.sort === 'desc' ? '-' : ''}`)
    return params.toString()
  }

  const { data, isLoading, mutate } = useSWR(
    `service-orders/products/my_products/?is_active=true&${buildQueryString()}`,
    fetcher
  )

  // search for suggestion. (which returns just the names)
  const { data: searchSuggestions } = useSWR(
    searchValue ? `service-orders/products/search_suggestions/?q=${searchValue}` : null,
    fetcher
  )

  const { data: currentUser, isLoading: loadingCurrentUser } = useSWR('users/me/', fetcher)
  const profileUser = currentUser || user
  const storefrontIdentifier = resolveStorefrontIdentifier(profileUser)
  const canShareStore = canShareStorefront(profileUser)

  const storeTitle =
    currentUser?.business_name ||
    currentUser?.owner_business_name ||
    user?.business_name ||
    'My store'

  const handleShareStorefront = async () => {
    if (!storefrontIdentifier) {
      ErrorNotification('Store link is unavailable for this account.')
      return
    }

    await shareStorefrontLink(storefrontIdentifier, {
      title: storeTitle,
      onError: (message) => ErrorNotification(message),
    })
  }

  const handleCopyStorefront = async () => {
    if (!storefrontIdentifier) {
      ErrorNotification('Store link is unavailable for this account.')
      return
    }

    await copyStorefrontLink(storefrontIdentifier, {
      onCopied: () => SuccessNotification('Store link copied to clipboard'),
      onError: (message) => ErrorNotification(message),
    })
  }


  const productlist = useMemo(() => {
    if (states?.productType === "All Types" || !states?.productType) {
      return data
    }

    return data?.filter((prod) => prod?.item_type?.toLowerCase() === states?.productType?.toLowerCase())
  }, [data, states])

  // Update filtered suggestions when search suggestions data changes
  useEffect(() => {
    if (searchSuggestions) {
      setFilteredSuggestions(searchSuggestions)
    }
  }, [searchSuggestions])

  const handleChange = (e) => {
    const newStates = { ...states, [e.target.name]: e.target.value }
    setStates(newStates)
  }

  const handleSearchChange = (value) => {
    setSearchValue(value)
    // Update the main search state immediately to trigger the search
    setStates(prev => ({ ...prev, search: value }))
  }

  const handleSearch = () => {
    // Trigger the search
    mutate()
  }

  const handleToggleVisibility = async (product) => {
    const isService = product?.item_type === 'service'
    const payload = { is_active: !product?.is_active }

    if (!isService) {
      const unitPrice = product?.unit_price
      if (unitPrice === null || unitPrice === undefined || unitPrice === '') {
        ErrorNotification("This product has no unit price. Please edit it and set a price before changing visibility.")
        return
      }
      payload.unit_price = parseFloat(unitPrice).toFixed(2)
    }

    try {
      const response = await api.patch(`service-orders/products/${product?.id}/`, payload)

      if (response.status === 200) {
        SuccessNotification(
          isService
            ? "Service visibility updated successfully"
            : "Product visibility updated successfully"
        )
        mutate()
      }
    } catch (error) {
      ErrorNotification(
        error.response?.data || "An error occurred while updating visibility"
      )
    }
  }

  const handleShareItem = async (item) => {
    if (!item?.is_active) {
      const itemLabel = item?.item_type === 'service' ? 'service' : 'product'
      ErrorNotification(`Enable visibility on this ${itemLabel} before sharing its marketplace link.`)
      return
    }

    await shareMarketplaceItemLink(item, {
      onCopied: () => SuccessNotification('Marketplace link copied to clipboard'),
      onError: (message) => ErrorNotification(message),
    })
  }

  const getDeleteErrorMessage = (error) => {
    const data = error?.response?.data
    if (!data) return 'Failed to delete item. Please try again.'
    if (typeof data === 'string') return data
    if (data.detail) return data.detail
    if (data.message) return data.message
    const firstKey = Object.keys(data)[0]
    if (firstKey && Array.isArray(data[firstKey])) return data[firstKey][0]
    return 'Failed to delete item. Please try again.'
  }

  const handleDelete = async (id) => {
    const isService = selectedProduct?.item_type === 'service'

    try {
      setIsDeleting(true)
      await api.delete(`service-orders/products/${id}/`)
      setShowDeleteModal(false)
      setSelectedProduct(null)
      SuccessNotification(isService ? 'Service deleted successfully' : 'Product deleted successfully')
      mutate()
    } catch (error) {
      ErrorNotification(getDeleteErrorMessage(error))
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      {showDeleteModal && (
        <DeleteGroupModal
          setShow={setShowDeleteModal}
          show={showDeleteModal}
          handleDelete={() => handleDelete(selectedProduct?.id)}
          title={selectedProduct?.item_type === 'service' ? 'Delete Service' : 'Delete Product Stock'}
          label={selectedProduct?.item_type === 'service' ? 'Service' : 'Product'}
          deleting={isDeleting}
          description={
            selectedProduct?.item_type === 'service'
              ? 'Are you sure you want to delete this service? It will be removed from the marketplace.'
              : 'Are you sure you want to delete this product from stock? It will be removed from the marketplace.'
          }
        />
      )}

      {showProductModal && (
        <ProductDetailModal
          setShow={setShowProductModal}
          show={showProductModal}
          product={selectedProduct}
        />
      )}

      {showServiceModal && (
        <ServiceDetailModal
          setShow={setShowServiceModal}
          show={showServiceModal}
          service={selectedProduct}
        />
      )}

      <div className="w-full lg:px-10 px-2 h-full py-10">
        <button onClick={() => history.goBack()} className="flex flex-row items-center">
          <IoIosArrowRoundBack size={25} className="mr-2 dark:text-white" />
          <p className="text-sm font-light capitalize dark:text-white">
            Back
          </p>
        </button>
        <div className="w-full space-y-6">
          {/* DASHBOARD HEADER */}
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between md:flex-row md:items-center md:justify-between mb-4">
            <DashboardHeader label="Inventory" />

            <div className="flex flex-col md:flex-row gap-2">
              <button
                type="button"
                onClick={handleShareStorefront}
                disabled={loadingCurrentUser || !canShareStore}
                className={`border px-6 flex flex-row items-center gap-2 py-2 rounded-md capitalize ${
                  !loadingCurrentUser && canShareStore
                    ? 'border-yellowcolor hover:border-yellowcolor/80 text-yellowcolor'
                    : 'border-gray-300 text-gray-400 cursor-not-allowed'
                }`}
              >
                <FiShare2 size={18} />
                Share Store Link
              </button>
              <button
                type="button"
                onClick={handleCopyStorefront}
                disabled={loadingCurrentUser || !canShareStore}
                className={`border px-6 flex flex-row items-center gap-2 py-2 rounded-md capitalize ${
                  !loadingCurrentUser && canShareStore
                    ? 'border-yellowcolor hover:border-yellowcolor/80 text-yellowcolor'
                    : 'border-gray-300 text-gray-400 cursor-not-allowed'
                }`}
              >
                <FiCopy size={18} />
                {loadingCurrentUser ? 'Loading...' : 'Copy Store Link'}
              </button>
              <button
                onClick={() => history.push("/admin/add-stock")}
                className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 rounded-md capitalize"
              >
                <FiPlusCircle size={20} />
                Add Product
              </button>
              <button
                onClick={() => history.push("/admin/add-service")}
                className="border border-yellowcolor hover:border-yellowcolor/80 text-yellowcolor px-6 flex flex-row items-center capitalize gap-2 py-2 rounded-md"
              >
                <FiPlusCircle size={20} />
                Add Service
              </button>
            </div>
          </div>

          <div className='w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
            <div className='w-full h-full bg-[#FFF8E1] dark:bg-[#FFF9E8] dark:border-[#FFF9E8] rounded-lg p-4'>
              <p className='text-sm text-black font-medium'>
                Active Products
              </p>
              <p className='text-lg text-black font-bold'>
                {data?.length || 0}
              </p>
            </div>

            <div className='w-full h-full bg-[#FFF8E1] dark:bg-[#FFF9E8] dark:border-[#FFF9E8] rounded-lg p-4'>
              <p className='text-sm text-black font-medium'>
                Average Performance
              </p>
              <p className='text-lg text-green-500 font-bold'>
                Good
              </p>
            </div>

            <div className='w-full h-full bg-[#FFF8E1] dark:bg-[#FFF9E8] dark:border-[#FFF9E8] rounded-lg p-4'>
              <p className='text-sm text-black font-medium'>
                Products Sold
              </p>
              <p className='text-lg text-black font-bold'>
                0<span className='text-xs font-normal ml-1'>items</span>
              </p>
            </div>

            <div className='w-full h-full bg-[#FFF8E1] dark:bg-[#FFF9E8] dark:border-[#FFF9E8] rounded-lg p-4'>
              <p className='text-sm text-black font-medium'>
                My Ratings
              </p>
              <p className='text-lg text-black font-bold'>
                0.0<span className='text-xs font-normal ml-1'>0 reviews</span>
              </p>
            </div>
          </div>

          <ServiceOrderFilter
            states={states}
            setStates={setStates}
            handleChange={handleChange}
            searchValue={searchValue}
            setSearchValue={handleSearchChange}
            onSearch={handleSearch}
            suggestions={filteredSuggestions}
            hideDateRange={true}
            placeholder="Search product"
          />

          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <Spinner />
            </div>
          ) : (
            <div className="min-w-0">
              <div className="my-5">
                <div className="overflow-x-auto w-full rounded-t-xl" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="flex flex-col divide-y divide-gray-200 min-w-[1000px] bg-white dark:bg-dark border dark:border-border-dark dark:text-white">
                  {productlist?.map((product, idx) => (
                    <div key={idx} className="flex flex-row items-center py-5 px-4 gap-2 text-[15px]">
                      {/* Product image */}
                      <div className="flex-shrink-0">
                        <img src={product?.images[0]?.image} alt="product" className="w-16 h-16 rounded-lg object-contain bg-gray-50" />
                      </div>
                      {/* Product name */}
                      <div className="truncate flex-1 min-w-[120px] pl-4 font-semibold text-black dark:text-white">
                        {product?.name}
                      </div>
                      {/* Divider */}
                      <div className="h-10 w-px bg-gray-200 mx-4" />
                      {/* Performance */}
                      <div className="flex flex-row items-center min-w-[170px] pr-4 gap-5">
                        <div className='flex flex-col'>
                          <div className="flex flex-row items-center gap-2">
                            <span className="text-xs text-gray-400 dark:text-white">Performance</span>
                            {/* <span className={`font-semibold text-sm ${product?.performance === 'Excellent' ? 'text-green-600' : product?.performance === 'Good' ? 'text-yellow-500' : 'text-orange-500'}`}>{product?.performance}</span> */}
                            <span className={`font-semibold text-sm text-green-600 dark:text-white`}>{product?.performance}</span>
                          </div>
                          <div className="flex flex-row items-center gap-2 text-xs text-gray-500 mt-1">
                            Total Sold <IoBagHandleOutline size={16} /> <span className="ml-1 font-bold text-black dark:text-white">{product?.sold || 0}</span>
                          </div>
                        </div>
                        <div className="mt-1">
                          <Gauge value={product?.performanceValue || 60} color={product?.performanceColor || 'green'} size={48} stroke={7} />
                        </div>
                      </div>
                      {/* Divider */}
                      <div className="h-10 w-px bg-gray-200 mx-4" />
                      {/* Stock/Service Type */}
                      <div className="flex flex-row items-center min-w-[90px] pr-4 gap-2">
                        <BsBox size={16} />
                        <div className='flex flex-col'>
                          <span className="text-xs text-gray-400">{product?.item_type === 'service' ? 'Type' : 'Stock'}</span>
                          <span className="font-semibold text-black text-base dark:text-white">
                            {product?.item_type === 'service' ? 'Service' : product?.quantity}
                          </span>
                        </div>
                      </div>
                      {/* Divider */}
                      <div className="h-10 w-px bg-gray-200 mx-4" />
                      {/* Price/Negotiation */}
                      <div className="flex flex-col items-center min-w-[120px] pr-4">
                        <span className="text-xs text-gray-400">{product?.item_type === 'service' ? 'Negotiable' : 'Product Price'}</span>
                        <span className="font-semibold text-black text-sm dark:text-white">
                          {product?.item_type === 'service'
                            ? (product?.is_negotiable ? 'Yes' : 'No')
                            : `₵ ${product?.unit_price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                          }
                        </span>
                      </div>
                      {/* Divider */}
                      <div className="h-10 w-px bg-gray-200 mx-4" />
                      {/* Visibility */}
                      <div className="flex flex-col items-center min-w-[90px] pr-4">
                        <span className="text-xs text-gray-400 mb-1 dark:text-white">Visibility</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={product?.is_active}
                            onChange={() => handleToggleVisibility(product)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-400"></div>
                        </label>
                      </div>
                      {/* Divider */}
                      <div className="h-10 w-px bg-gray-200 mx-4" />
                      {/* Actions */}
                      <div className="flex flex-row items-center gap-4 min-w-[80px]">
                        <Tooltip text="View" position="top">
                          <button
                            onClick={() => {
                              setSelectedProduct(product)
                              if (product?.item_type === 'service') {
                                setShowServiceModal(true)
                              } else {
                                setShowProductModal(true)
                              }
                            }}
                            className="">
                            <FiEye size={16} />
                          </button>
                        </Tooltip>
                        <Tooltip
                          text={
                            product?.item_type === 'service'
                              ? 'Share service marketplace link'
                              : 'Share product marketplace link'
                          }
                          position="top"
                        >
                          <button
                            type="button"
                            onClick={() => handleShareItem(product)}
                            className={!product?.is_active ? 'opacity-40 cursor-not-allowed' : ''}
                            disabled={!product?.is_active}
                          >
                            <FiShare2 size={16} />
                          </button>
                        </Tooltip>
                        <Tooltip text="Edit" position="top">
                          <button
                            type="button"
                            onClick={() => {
                              if (product?.item_type === 'service') {
                                history.push("/admin/edit-service", {
                                  state: {
                                    ...product
                                  }
                                })
                              } else {
                                history.push("/admin/edit-stock", {
                                  state: {
                                    ...product
                                  }
                                })
                              }
                            }}
                            className="">
                            <RxPencil1 size={16} />
                          </button>
                        </Tooltip>
                        <Tooltip text="Delete" position="top">
                          <button
                            onClick={() => {
                              setSelectedProduct(product)
                              setShowDeleteModal(true)
                            }}
                            className="">
                            <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M18 4.98307C15.225 4.70807 12.4333 4.56641 9.65 4.56641C8 4.56641 6.35 4.64974 4.7 4.81641L3 4.98307" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                              <path d="M7.58398 4.14102L7.76732 3.04935C7.90065 2.25768 8.00065 1.66602 9.40898 1.66602H11.5923C13.0007 1.66602 13.109 2.29102 13.234 3.05768L13.4173 4.14102" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                              <path d="M16.2077 7.61523L15.666 16.0069C15.5744 17.3152 15.4993 18.3319 13.1743 18.3319H7.82435C5.49935 18.3319 5.42435 17.3152 5.33268 16.0069L4.79102 7.61523" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                              <path d="M9.10742 13.75H11.8824" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                              <path d="M8.41602 10.416H12.5827" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
                {/* Pagination */}
                {data?.length > 15 && <div className="bg-white border-t border-gray-200 p-3 flex flex-row items-center justify-between w-full rounded-b-xl min-w-[1000px]">
                  <button className="px-4 py-1.5 flex flex-row items-center gap-2 bg-gray-100 hover:bg-gray-200 border-md text-black border rounded-lg font-light text-sm border-gray-300">
                    &larr; Previous
                  </button>
                  <div className="">
                    <ul className="flex flex-row items-center gap-0.5">
                      {Array.from({ length: 10 }).map((_, index) => (
                        <li key={index}>
                          <a
                            className={`mx-1 flex h-9 w-9 hover:bg-gray-200 items-center justify-center rounded-full p-0 text-sm text-blue-gray-500 transition duration-150 ease-in-out ${index === 0 ? "bg-gray-100" : "bg-transparent"}`}
                            href="#"
                          >
                            {`${index + 1}`}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button className="px-4 py-1.5 flex flex-row items-center gap-2 bg-gray-100 hover:bg-gray-200 border-md text-black border rounded-lg font-light text-sm border-gray-300">
                    Next &rarr;
                  </button>
                </div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default ServiceOrdersInventory